import React, { useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ReportRoundedIcon from "@mui/icons-material/ReportRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import SyncAltRoundedIcon from "@mui/icons-material/SyncAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { Trans, useTranslation } from "react-i18next";

const COLUMN_MARKERS = [
  "ID da subcategoria",
  "ID da categoria",
  "Preço com desconto",
  "Série caminhão",
  "Série ônibus",
  "Subcategoria",
  "Categoria",
  "Descrição",
  "Comprimento",
  "Largura",
  "Altura",
  "Imagens",
  "Itens inclusos",
  "Estoque",
  "Status",
  "Preço",
  "Nome",
  "Tipo",
  "Peso",
  "SKU",
];

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const COLUMN_PATTERN = new RegExp(`(${COLUMN_MARKERS.map(escapeRe).join("|")}|\\bid\\b)`);

const SECTIONS = [
  { key: "required", icon: ReportRoundedIcon, color: "error", wide: true },
  { key: "identity", icon: BadgeRoundedIcon, color: "primary", wide: true },
  { key: "prices", icon: PaymentsRoundedIcon, color: "success" },
  { key: "category", icon: CategoryRoundedIcon, color: "secondary" },
  { key: "measures", icon: StraightenRoundedIcon, color: "info" },
  { key: "application", icon: LocalShippingRoundedIcon, color: "warning" },
  { key: "images", icon: ImageRoundedIcon, color: "info", wide: true },
  { key: "included", icon: Inventory2RoundedIcon, color: "success", wide: true },
  { key: "update", icon: SyncAltRoundedIcon, color: "warning", wide: true },
];

const STEPS = [
  { key: "step1", icon: DownloadRoundedIcon },
  { key: "step2", icon: VisibilityRoundedIcon },
  { key: "step3", icon: SendRoundedIcon },
];

const paletteOf = (theme, color) => theme.palette[color] || theme.palette.primary;

const HighlightedText = ({ text }) => {
  const parts = String(text || "").split(COLUMN_PATTERN);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part === "id" || COLUMN_MARKERS.includes(part)) {
      return (
        <Box
          key={`${part}-${i}`}
          component="strong"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.06),
            px: 0.55,
            py: 0.05,
            borderRadius: 0.75,
            fontSize: "0.92em",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {part}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const ImportGuideDialog = ({ open, reading, onClose, onDownloadTemplate, onPickFile }) => {
  const { t } = useTranslation("products");
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || reading) return;
    onPickFile?.(file);
  };

  return (
    <Dialog
      open={open}
      onClose={reading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            color: "primary.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        >
          <InsertDriveFileRoundedIcon />
        </Box>
        <Box>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.25, fontWeight: 700 }}>
            {t("importGuide.title")}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
            {t("importGuide.formats")}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: "72vh", bgcolor: (theme) => alpha(theme.palette.text.primary, 0.015) }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.25,
            p: 1.5,
            mb: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          }}
        >
          <InfoOutlinedIcon color="primary" sx={{ mt: 0.15, fontSize: 20, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ lineHeight: 1.55, color: "text.primary" }}>
            {t("importGuide.intro")}
          </Typography>
        </Box>

        <Typography
          variant="overline"
          sx={{ display: "block", mb: 1, letterSpacing: 1, color: "text.secondary", fontWeight: 700 }}
        >
          {t("importGuide.howTitle")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 1.25,
            mb: 3,
          }}
        >
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            return (
              <Box
                key={step.key}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  minHeight: 118,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      fontSize: 12,
                      fontWeight: 800,
                      color: "primary.contrastText",
                      bgcolor: "primary.main",
                    }}
                  >
                    {idx + 1}
                  </Box>
                  <Icon sx={{ fontSize: 18, color: "primary.main" }} />
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.5, color: "text.primary" }}>
                  {step.key === "step1" ? (
                    <Trans
                      i18nKey="importGuide.step1"
                      ns="products"
                      components={{
                        download: (
                          <Box
                            component="a"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!reading) onDownloadTemplate?.();
                            }}
                            sx={{
                              color: reading ? "text.disabled" : "primary.main",
                              fontWeight: 700,
                              textDecoration: "underline",
                              textUnderlineOffset: "3px",
                              pointerEvents: reading ? "none" : "auto",
                              cursor: reading ? "default" : "pointer",
                              "&:hover": reading ? undefined : { color: "primary.dark" },
                            }}
                          />
                        ),
                      }}
                    />
                  ) : (
                    t(`importGuide.${step.key}`)
                  )}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography
          variant="overline"
          sx={{ display: "block", mb: 1, letterSpacing: 1, color: "text.secondary", fontWeight: 700 }}
        >
          {t("importGuide.fieldsTitle")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.25,
            mb: 2.5,
          }}
        >
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const body = t(`importGuide.sections.${section.key}.body`);
            const lines = String(body)
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);
            return (
              <Box
                key={section.key}
                sx={{
                  gridColumn: section.wide ? { sm: "1 / -1" } : undefined,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: (theme) => alpha(paletteOf(theme, section.color).main, 0.28),
                  boxShadow: (theme) => `inset 3px 0 0 ${paletteOf(theme, section.color).main}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.25,
                      display: "grid",
                      placeItems: "center",
                      color: (theme) => paletteOf(theme, section.color).main,
                      bgcolor: (theme) => alpha(paletteOf(theme, section.color).main, 0.12),
                    }}
                  >
                    <Icon sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {t(`importGuide.sections.${section.key}.title`)}
                  </Typography>
                </Box>
                <Box component="ul" sx={{ m: 0, pl: 2.25, display: "flex", flexDirection: "column", gap: 0.7 }}>
                  {lines.map((line) => (
                    <Typography
                      key={`${section.key}-${line}`}
                      component="li"
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.55, pl: 0.25 }}
                    >
                      <HighlightedText text={line} />
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            p: 1.75,
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
            <TableChartRoundedIcon sx={{ mt: 0.15, color: "text.secondary", fontSize: 22, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {t("importGuide.columnsHint")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            onClick={onDownloadTemplate}
            disabled={reading}
            sx={{ textTransform: "none", flexShrink: 0, fontWeight: 600 }}
          >
            {t("importGuide.download")}
          </Button>
        </Box>

        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          hidden
          onChange={handleFile}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.75 }}>
        <Button onClick={onClose} disabled={reading} sx={{ textTransform: "none" }}>
          {t("importGuide.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={reading ? <CircularProgress size={16} color="inherit" /> : <FileUploadRoundedIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={reading}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {reading ? t("importGuide.reading") : t("importGuide.choose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportGuideDialog;
